import React, { useMemo } from 'react';
import { DialogMessageNotification, DialogMessageRenderer, MessageWithActionsLayout } from '@cp/entities/dialog';
import { MessageType } from '@common/model/dialog/message/messageType';
import { useCurrentUser } from '@cp/entities/currentUser';
import { DialogMemberType } from '@common/model/dialog/dialogMemberType';
import { Typography } from '@cp/ds/src/components/typography';
import { ApiServerEntityIDType } from '@common/types';
import { useClientSubscription } from '@cp/entities/client';
import { ClientContactsPreview } from '@cp/entities/clientContact';
import { Link } from '@cp/ds/src/components/link';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { ClientContactsDictionary } from '@common/model/client';
import { UserType } from '@cp/entities/user';
import { ClientContactType } from '@common/model/client/clientContactType';
import { coreClientContactApi } from 'src/entities/clientContact';
import { useStartBillingSubscription } from 'src/features/billing';
import { useExternalMessangerResponseTemplate } from '../../model';

export const ClientContactsRenderer: DialogMessageRenderer<MessageType.ClientContacts> = (props) => {
  const { message, isPreview } = props;
  const { contactTypes } = message.payload;
  const currentUser = useCurrentUser();
  const { data: { isPayedAndNotExpired } = {}, isLoading: isLoadingSubscription } = useClientSubscription();
  const [startSubscription] = useStartBillingSubscription();
  const responseTemplate = useExternalMessangerResponseTemplate();

  const linkParams = useMemo(() => {
    if (!responseTemplate) {
      return undefined;
    }
    return {
      [ClientContactType.WhatsApp]: {
        text: responseTemplate,
      },
    };
  }, [responseTemplate]);

  const isClient = currentUser.type === UserType.Client;

  const isSelfContacts = isClient && message.author.type === DialogMemberType.Client && message.author.entityId === currentUser.id;

  const isSubscriptionRequired = isClient && !isSelfContacts;

  const { data: contactsData, isLoading: isContactsLoading } = coreClientContactApi.endpoints.getAnotherClientContacts.useQuery(
    {
      clientId: message.author.entityId as ApiServerEntityIDType,
    },
    {
      skip: !message.author.entityId || isSelfContacts || (isSubscriptionRequired && !isPayedAndNotExpired),
    },
  );

  const contacts = React.useMemo(
    () =>
      contactsData?.contacts?.reduce<ClientContactsDictionary>((acc, { type, value }) => {
        acc[type] = value;
        return acc;
      }, {}),
    [contactsData?.contacts],
  );

  if (isSelfContacts) {
    return null;
  }

  if (isSubscriptionRequired && !isPayedAndNotExpired) {
    return (
      <DialogMessageNotification
        {...props}
        text={
          <>
            Чтобы увидеть контакты собеседника, подключите{' '}
            <Link color="pink" onClick={startSubscription} sx={{ typography: 'caption bold' }}>
              премиум-доступ
            </Link>
          </>
        }
      />
    );
  }

  if (isPreview) {
    return <>(контакты отправлены)</>;
  }

  return (
    <MessageWithActionsLayout>
      {isLoadingSubscription || isContactsLoading || !contactsData ? (
        <Skeleton width={200} />
      ) : (
        <>
          <Typography variant="caption bold">Свяжитесь с нанимателем</Typography>
          <ClientContactsPreview
            contactsToShow={contactTypes}
            data={contacts}
            hideEmpty
            interactive
            linkParams={linkParams}
            sx={{ mt: 1 }}
          />
        </>
      )}
    </MessageWithActionsLayout>
  );
};
